/**
 * Time Tracking Entity Types
 * Defines the time tracking structure for Agile teams
 */

export interface TimeLog {
  id: string;
  taskId: string;
  userId: string;
  userName?: string;
  taskName?: string;
  hours: number;
  minutes: number;
  totalMinutes: number; // Total time in minutes
  notes?: string;
  date: string; // ISO 8601 date string
  startTime?: string; // ISO 8601 timestamp for timer
  endTime?: string; // ISO 8601 timestamp for timer
  isActive?: boolean; // Is timer currently running
  createdAt: string;
  updatedAt?: string;
}

export interface ActiveTimer {
  id?: string;
  taskId: string;
  taskName: string;
  startTime: string; // ISO 8601 timestamp
  elapsedSeconds: number;
  isPaused: boolean;
  pausedAt?: string;
  notes?: string;
}

export interface TimeSummary {
  totalHours: number;
  totalMinutes: number;
  logCount: number;
  byTask: TaskTimeSummary[];
  bySprint?: SprintTimeSummary[];
  byUser?: UserTimeSummary[];
}

export interface TaskTimeSummary {
  taskId: string;
  taskName: string;
  totalMinutes: number;
  logCount: number;
  estimatedMinutes?: number;
  percentComplete?: number;
}

export interface SprintTimeSummary {
  sprintId: string;
  sprintName: string;
  totalMinutes: number;
  estimatedMinutes?: number;
  taskCount: number;
  completedTasks: number;
}

export interface UserTimeSummary {
  userId: string;
  userName: string;
  totalMinutes: number;
  logCount: number;
  taskCount: number;
}

export interface TimeReport {
  period: 'day' | 'week' | 'sprint' | 'month' | 'custom';
  startDate: string;
  endDate: string;
  summary: TimeSummary;
  logs: TimeLog[];
  estimatedVsActual?: EstimateComparison[];
}

export interface EstimateComparison {
  taskId: string;
  taskName: string;
  estimated: number; // minutes
  actual: number; // minutes
  difference: number; // minutes
  percentDifference: number; // percentage
  status: 'under' | 'on-track' | 'over';
}

export interface TimeTrackingFilters {
  taskId?: string;
  userId?: string;
  sprintId?: string;
  startDate?: string;
  endDate?: string;
  isActive?: boolean;
}

// DTOs for API requests
export interface CreateTimeLogDTO {
  taskId: string;
  hours: number;
  minutes: number;
  notes?: string;
  date?: string; // Defaults to now
}

export interface UpdateTimeLogDTO {
  hours?: number;
  minutes?: number;
  notes?: string;
  date?: string;
}

export interface StartTimerDTO {
  taskId: string;
  notes?: string;
}

export interface StopTimerDTO {
  notes?: string;
}

// Redux State
export interface TimeTrackingState {
  logs: TimeLog[];
  activeTimer: ActiveTimer | null;
  summary: TimeSummary | null;
  currentReport: TimeReport | null;
  loading: boolean;
  error: string | null;
  filters: TimeTrackingFilters;
}

// Constants
export const TIME_TRACKING_ROLES = {
  DEVELOPER: 'developer',
  SCRUM_MASTER: 'scrum_master',
  ADMIN: 'admin',
  MANAGER: 'manager',
} as const;

export type TimeTrackingRole = typeof TIME_TRACKING_ROLES[keyof typeof TIME_TRACKING_ROLES];

// Helper function to convert hours and minutes to total minutes
export const toTotalMinutes = (hours: number, minutes: number): number => {
  return hours * 60 + minutes;
};

// Helper function to convert total minutes to hours and minutes
export const fromTotalMinutes = (totalMinutes: number): { hours: number; minutes: number } => {
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  return { hours, minutes };
};

// Helper function to format duration
export const formatDuration = (totalMinutes: number): string => {
  const { hours, minutes } = fromTotalMinutes(totalMinutes);
  if (hours === 0) {
    return `${minutes}m`;
  }
  if (minutes === 0) {
    return `${hours}h`;
  }
  return `${hours}h ${minutes}m`;
};

// Helper function to calculate elapsed time
export const calculateElapsedSeconds = (startTime: string, pausedSeconds: number = 0): number => {
  const start = new Date(startTime).getTime();
  const now = Date.now();
  const elapsed = Math.floor((now - start) / 1000);
  return elapsed - pausedSeconds;
};
